import type { LucideIcon } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * One headline number.
 *
 * A tile, not a one-bar chart: four figures that share no scale and are not compared to each other
 * have nothing for a chart to encode. The label says what it is, the number says how many, and an
 * icon gives the eye something to find the tile by.
 *
 * The value carries the font's proportional figures deliberately — `tabular-nums` gives every
 * digit the width of a zero, which at this size makes a number like 121 read as though it had been
 * spaced out by hand. Tabular figures are for columns that have to line up, not for a single
 * number standing on its own.
 */
export function StatTile({
  label,
  value,
  icon: Icon,
  to,
  hint,
  loading,
}: {
  label: string
  value: number
  icon: LucideIcon
  /** Where the number can be looked into. A figure nobody can act on is decoration. */
  to?: string
  hint?: string
  loading?: boolean
}) {
  const body = (
    <>
      <div className="flex items-center justify-between gap-2">
        <p className="text-muted-foreground text-sm">{label}</p>
        <Icon className="text-muted-foreground size-4 shrink-0" aria-hidden />
      </div>
      {loading ? (
        <Skeleton className="h-8 w-16" />
      ) : (
        <p className="text-3xl leading-none font-semibold">{value}</p>
      )}
      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
    </>
  )

  // `Card` is a plain div with no `asChild`, so the link wraps it rather than replacing it.
  const card = <Card className="grid h-full gap-2 p-4">{body}</Card>

  return to ? (
    <Link to={to} className="hover:[&>*]:bg-accent/40 rounded-xl transition-colors">
      {card}
    </Link>
  ) : (
    card
  )
}
